import { useBackendApi } from './backendApi';
import { useEventsPublisher } from 'event-bus';
import { useObjectDatesMapper, useDateHelper } from './Helper/dateHelper';

export enum AdditionalWorkflowDateOption {
  USE_TRANSACTION_DATES = 'USE_TRANSACTION_DATES',
  CUSTOM_DATES = 'CUSTOM_DATES',
  UPDATE_TRANSACTION_DATES = 'UPDATE_TRANSACTION_DATES',
}

export enum UpdateProjectDatesOption {
  ADJUST_TASKS_DATES = 'ADJUST_TASKS_DATES',
  DO_NOT_CHANGE_DATES = 'DO_NOT_CHANGE_DATES',
}

export type ProjectPlan = {
  id: number
  name: string
  hasGroups: boolean
  _sourcePlan?: {
    id: number,
    name: string,
  }
  _sourcePlans: {
    id: number,
    name: string,
  }[]
  excludedGroupsIds: number[]
  appliedAt?: Date | null
  dateOption?: AdditionalWorkflowDateOption | null
  customStartDate?: Date | null
  customEndDate?: Date | null
  existingTasksDatesOption?: UpdateProjectDatesOption | null
}

export enum DetailFieldLinkResolution {
  OVERWRITE = 'OVERWRITE',
  KEEP_EXISTING = 'KEEP_EXISTING',
}

export type NewTaskDueDateInfo = {
  dueDateDays: number | null;
  dueDateWhen: string | null;
  parentTaskDays: number | null;
  parentTaskName: string | null;
  parentTaskIsProceeding: boolean | null;
  calculatedDueDate: string | null;
}

export type DetailFieldConflict = {
  rootFieldId: number;
  rootFieldName: string;
  existingTaskId: number;
  existingTaskName: string;
  existingLinkedFieldName: string;
  existingFieldValue: string | null;
  newTaskId: number;
  newTaskName: string;
  newLinkedFieldName: string;
  newTaskDueDateInfo: NewTaskDueDateInfo | null;
  affectedNewTaskIds: number[];
  currentResolution: DetailFieldLinkResolution;
}

export interface ProjectPlanService {
  getPlanByProjectId: (projectId: number) => Promise<ProjectPlan>;
  getProjectPlans: (projectId: number) => Promise<ProjectPlan[]>;
  addNewPlansToProject: (
    projectId: number,
    plansIds: number[],
    dateOption: AdditionalWorkflowDateOption,
    customStartDate?: Date,
    customEndDate?: Date,
    existingTasksDatesOption?: UpdateProjectDatesOption,
  ) => Promise<ProjectPlan>;
  excludeGroupFromPrimaryPlan: (projectId: number, groupId: number) => Promise<void>;
  includeGroupToPrimaryPlan: (projectId: number, groupId: number) => Promise<void>;
  excludeGroupFromPlan: (projectId: number, planId: number, groupId: number) => Promise<void>;
  includeGroupToPlan: (projectId: number, planId: number, groupId: number) => Promise<void>;
  getConflicts: (projectId: number, projectPlanId: number) => Promise<DetailFieldConflict[]>;
  setConflictResolutions: (
    projectId: number,
    projectPlanId: number,
    resolutions: Record<number, DetailFieldLinkResolution>,
  ) => Promise<void>;
  applyAdditionalProjectPlan: (projectId: number, projectPlanId: number) => Promise<void>;
}

export const INCLUDED_GROUPS_CHANGED = 'INCLUDED_GROUPS_CHANGED';
export const ADDITIONAL_PROJECT_PLAN_CREATED = 'ADDITIONAL_PROJECT_PLAN_CREATED';
export const ADDITIONAL_PROJECT_PLAN_APPLIED = 'ADDITIONAL_PROJECT_PLAN_APPLIED';

export function useProjectPlanService() {
  const backendApi = useBackendApi();

  const eventsPublisher = useEventsPublisher();

  const dateHelper = useDateHelper();

  const datesMapper = useObjectDatesMapper<ProjectPlan>(
    ['customStartDate', 'customEndDate'],
    ['appliedAt'],
  );

  const getPlanByProjectId = async (projectId: number) => {
    const result = await backendApi.get(`/projects/${projectId}/plan`);
    return datesMapper.mapSingle(result as Record<string, unknown>);
  };

  const getProjectPlans = async (projectId: number) => {
    const result = await backendApi.get(`/projects/${projectId}/plans`);
    return datesMapper.mapList(result as Record<string, unknown>[]);
  };

  const addNewPlansToProject = async (
    projectId: number,
    plansIds: number[],
    dateOption: AdditionalWorkflowDateOption,
    customStartDate?: Date,
    customEndDate?: Date,
    existingTasksDatesOption?: UpdateProjectDatesOption,
  ) => {
    const body: Record<string, unknown> = { plansIds, dateOption };
    if (customStartDate) {
      body.customStartDate = dateHelper.toYmd(customStartDate);
    }
    if (customEndDate) {
      body.customEndDate = dateHelper.toYmd(customEndDate);
    }
    if (existingTasksDatesOption) {
      body.existingTasksDatesOption = existingTasksDatesOption;
    }
    const result = await backendApi.post(`/projects/${projectId}/plans`, body);
    const mapped = datesMapper.mapSingle(result as Record<string, unknown>);
    eventsPublisher.publish(ADDITIONAL_PROJECT_PLAN_CREATED, { projectId });
    return mapped;
  };

  const excludeGroupFromPrimaryPlan = async (projectId: number, groupId: number) => {
    await backendApi.delete(`/projects/${projectId}/plan/tasksgroups/${groupId}`);
    eventsPublisher.publish(INCLUDED_GROUPS_CHANGED, { projectId });
  };

  const includeGroupToPrimaryPlan = async (projectId: number, groupId: number) => {
    await backendApi.post(`/projects/${projectId}/plan/tasksgroups/${groupId}`);
    eventsPublisher.publish(INCLUDED_GROUPS_CHANGED, { projectId });
  };

  const excludeGroupFromPlan = async (projectId: number, planId: number, groupId: number) => {
    await backendApi.delete(`/projects/${projectId}/plans/${planId}/tasksgroups/${groupId}`);
    eventsPublisher.publish(INCLUDED_GROUPS_CHANGED, { projectId });
  };

  const includeGroupToPlan = async (projectId: number, planId: number, groupId: number) => {
    await backendApi.post(`/projects/${projectId}/plans/${planId}/tasksgroups/${groupId}`);
    eventsPublisher.publish(INCLUDED_GROUPS_CHANGED, { projectId });
  };

  const getConflicts = async (projectId: number, projectPlanId: number) => {
    return await backendApi.get(
      `/projects/${projectId}/plans/${projectPlanId}/conflicts`,
    ) as DetailFieldConflict[];
  };

  const setConflictResolutions = async (
    projectId: number,
    projectPlanId: number,
    resolutions: Record<number, DetailFieldLinkResolution>,
  ) => {
    await backendApi.put(
      `/projects/${projectId}/plans/${projectPlanId}/conflict-resolutions`,
      { resolutions },
    );
  };

  const applyAdditionalProjectPlan = async (
    projectId: number,
    projectPlanId: number,
  ) => {
    await backendApi.post(
      `/projects/${projectId}/plans/${projectPlanId}/apply`,
    );
    eventsPublisher.publish(ADDITIONAL_PROJECT_PLAN_APPLIED, { projectId });
  };

  return {
    getPlanByProjectId,
    getProjectPlans,
    addNewPlansToProject,
    excludeGroupFromPrimaryPlan,
    includeGroupToPrimaryPlan,
    excludeGroupFromPlan,
    includeGroupToPlan,
    getConflicts,
    setConflictResolutions,
    applyAdditionalProjectPlan,
  };
}
